import { Fragment, useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Compass, ShoppingBag } from 'lucide-react';
import { useCart } from 'components/commerce/CartProvider';
import NavigationAccount from 'components/access/NavigationAccount';
import NavigationTms from 'components/access/NavigationTms';
import NavigationThemeSwitch from './NavigationThemeSwitch';
import PhoneNavigation from './PhoneNavigation';
import { usePhoneNavigation } from 'hooks/usePhoneNavigation';
import './Navigation.css';

const NAV_LINKS = [
  { label: 'Paperwork', path: '/paperwork' },
  { label: 'Services', path: '/services' },
  { label: 'Courses', path: '/webinars' },
  { label: 'TMS', path: '/tms' },
  { label: 'Blog', path: '/blog' },
  { label: 'Work With Us', path: '/work-with-us' },
  { label: 'Partner With Us', path: '/partner-with-us' },
  { label: 'About', path: '/about' },
];

const LEFT_NAV_LINKS = NAV_LINKS.filter((link) => ['Paperwork', 'Services', 'Courses', 'TMS'].includes(link.label));
const RIGHT_NAV_LINKS = NAV_LINKS.filter((link) => ['Blog', 'Work With Us', 'Partner With Us', 'About'].includes(link.label));

// Fired by Add to cart; the nav cart badge answers with one short gold pulse.
const CART_ADDED_EVENT = 'magellan:cart-added';

function slug(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function isActivePath(current, target) {
  if (target === '/') {
    return current === '/';
  }
  return current === target || current.startsWith(`${target}/`);
}

function isActiveNavLink(current, link) {
  return isActivePath(current, link.path);
}

export default function Navigation() {
  const phone = usePhoneNavigation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [cartPulse, setCartPulse] = useState(0);
  const navRef = useRef(null);
  const location = useLocation();
  const { itemCount } = useCart();

  useEffect(() => {
    const handleCartAdded = () => setCartPulse((count) => count + 1);
    window.addEventListener(CART_ADDED_EVENT, handleCartAdded);
    return () => window.removeEventListener(CART_ADDED_EVENT, handleCartAdded);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setMobileOpen(false);
  }, [location.pathname, phone]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const renderDesktopNavLink = (link) => {
    const active = isActiveNavLink(location.pathname, link);
    const linkClassName = `nav-link whitespace-nowrap text-[10px] uppercase tracking-[0.18em] 2xl:tracking-[0.22em] font-body font-medium transition-colors duration-300 ${
      active ? 'text-accent-ink' : 'text-copy/60 hover:text-ink'
    }`;

    if (link.children?.length) {
      const dropdownOpen = openDropdown === link.path;

      return (
        <div key={link.path} className="relative">
          <button
            type="button"
            data-testid={`nav-link-${slug(link.label)}`}
            className={`${linkClassName} appearance-none border-0 bg-transparent p-0`}
            aria-haspopup="menu"
            aria-expanded={dropdownOpen}
            onClick={() => setOpenDropdown((current) => (current === link.path ? null : link.path))}
          >
            {link.label}
          </button>
          <div
            data-testid={`nav-dropdown-${slug(link.label)}`}
            className={`absolute left-1/2 top-full z-50 mt-4 min-w-[250px] -translate-x-1/2 border border-gold/15 bg-canvas/95 p-3 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-2xl transition-all duration-300 ${
              dropdownOpen
                ? 'pointer-events-auto translate-y-0 opacity-100'
                : 'pointer-events-none opacity-0'
            }`}
            role="menu"
          >
            {link.children.map((child) => (
              <Link
                key={child.path}
                to={child.path}
                data-testid={`nav-link-${slug(child.label)}`}
                className="block px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-copy/58 transition-colors duration-300 hover:text-accent-ink"
                role="menuitem"
                onClick={() => setOpenDropdown(null)}
              >
                {child.label}
              </Link>
            ))}
          </div>
        </div>
      );
    }

    return (
      <Link
        key={link.path}
        to={link.path}
        data-testid={`nav-link-${slug(link.label)}`}
        className={linkClassName}
      >
        {link.label}
      </Link>
    );
  };

  const logo = (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
      <div className="relative">
        <Compass className="w-7 h-7 text-accent-ink transition-all duration-500 group-hover:rotate-[360deg]" strokeWidth={1.5} />
        <div className="logo-accent-glow absolute inset-0 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
      <div className="flex items-center gap-2">
        <span className="font-display text-xl md:text-2xl tracking-[0.3em] text-ink font-bold">MAGELLAN</span>
        <span className="hidden sm:inline-block ornament-diamond ml-1" />
      </div>
    </Link>
  );
  if (phone) return <>
    <nav ref={navRef} data-testid="main-navigation" className="site-navigation fixed top-0 left-0 right-0 z-50" data-scrolled={scrolled} data-phone="true">
      <div className="site-navigation-inner h-16">{logo}</div>
    </nav>
    <PhoneNavigation links={NAV_LINKS} itemCount={itemCount} cartPulse={cartPulse} />
  </>;

  return (
    <nav
      ref={navRef}
      data-testid="main-navigation"
      className="site-navigation fixed top-0 left-0 right-0 z-50"
      data-scrolled={scrolled}
    >
      <div className="site-navigation-inner max-w-[1540px] mx-auto px-4 sm:px-6 md:px-8 xl:px-10 2xl:px-12 flex items-center justify-between h-16 md:h-20 gap-8">
        <div className="hidden min-[1440px]:flex items-center justify-end gap-5 2xl:gap-7 flex-1 min-w-0">
          {LEFT_NAV_LINKS.map(renderDesktopNavLink)}
        </div>

        {logo}

        <div className="site-navigation-links-right hidden min-[1440px]:flex items-center justify-start gap-5 2xl:gap-7 flex-1 min-w-0">
          {RIGHT_NAV_LINKS.map(renderDesktopNavLink)}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="min-[1440px]:hidden text-ink p-2"
          aria-label="Toggle navigation"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <NavigationThemeSwitch />
      <NavigationTms onOpen={() => { setMobileOpen(false); setOpenDropdown(null); }} />
      <NavigationAccount onOpen={() => { setMobileOpen(false); setOpenDropdown(null); }} />
      <Link to="/cart" data-testid="nav-cart" className="site-navigation-cart" title="Cart" onClick={() => setMobileOpen(false)}
        aria-label={'Shopping cart, ' + itemCount + (itemCount === 1 ? ' item' : ' items')} aria-current={location.pathname === '/cart' ? 'page' : undefined}>
        <ShoppingBag size={21} strokeWidth={1.5} aria-hidden="true" />
        {/* A new key restarts the one-time pulse on every Add to cart. */}
        {itemCount > 0 && <span key={cartPulse} className={cartPulse ? 'site-navigation-cart-pulse' : undefined} aria-hidden="true">{itemCount}</span>}
      </Link>

      <div
        data-testid="mobile-menu"
        aria-hidden={!mobileOpen}
        inert={mobileOpen ? undefined : ''}
        className={`min-[1440px]:hidden overflow-hidden transition-all duration-500 ease-out ${
          mobileOpen ? 'max-h-[720px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-8 flex flex-col gap-1 border-t border-gold/10">
          <NavigationThemeSwitch mobile />
          {NAV_LINKS.map((link, i) => (
            <Fragment key={link.path}>
              <Link
                to={link.path}
                data-testid={`mobile-nav-link-${slug(link.label)}`}
                onClick={() => setMobileOpen(false)}
                className={`text-[11px] uppercase tracking-[0.22em] font-body py-3 px-4 transition-all duration-300 ${
                  isActiveNavLink(location.pathname, link)
                    ? 'text-accent-ink bg-gold/5'
                    : 'text-copy/60 hover:text-ink hover:bg-ink/[0.02]'
                }`}
                style={{ transitionDelay: `${i * 35}ms` }}
              >
                {link.label}
              </Link>
              {link.children?.map((child) => (
                <Link
                  key={child.path}
                  to={child.path}
                  data-testid={`mobile-nav-link-${slug(child.label)}`}
                  onClick={() => setMobileOpen(false)}
                  className="ml-4 border-l border-gold/10 px-4 py-2 font-body text-[10px] uppercase tracking-[0.2em] text-copy/42 transition-colors duration-300 hover:text-accent-ink"
                >
                  {child.label}
                </Link>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
    </nav>
  );
}
